import moment from "moment";
import React, { useMemo, useState } from "react";
import Button1 from "../../form/Button1";
import { PriceBreakdown } from "./PriceBreakdown";
import { enqueueSnackbar } from "notistack";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";
import setRefundFee from "../../../controllers/booking/postTicketing/refund/setRefundFee";
import { Modal3 } from "../../DIsplay/Modal/Modal3";
import ExpiryInput from "./ExpiryInput";
import { formatMoney } from "../../../features/utils/formatMoney";
import {
  hasRefundQuote,
  isRefundNotApprovedError,
  isRefundQuotedStatus,
} from "../../../data/flight/order/getRefundAdminGuidance";
import {
  getPaxTypeUnitPricing,
  toOriginalTicketPassengerPrice,
} from "./PriceBreakdown";
import RelatedDocsPanel from "../Change/RelatedDocsPanel";
import {
  getIntraverseMarkupFromBreakdown,
  getRefundQuoteSummary,
  isRefundAmountLine,
  isRefundTaxLine,
  resolveOriginalAppliedAdjustment,
  reverseOriginalAdjustment,
} from "../../../utils/booking/refundQuote";

export default function RefundQuote({ data, reload }) {
  const [details, setDetails] = useState("");
  const [loadings, setLoadings] = useState({ fetchStaff: false });
  const [feeData, setFeeData] = useState({
    penality: data?.fee || 0,
    ticketDifference: data?.ticketDifference || 0,
    passengerPrice: [],
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [paymentDeadline, setPaymentDeadline] = useState(
    moment().add(3, "hours").toISOString(),
  );
  // Backend: when true, configured refund markup is subtracted from agent refund.
  // Keeps existing Ops Fixed/% UI; this flag is separate.
  const [applyMarkup, setApplyMarkup] = useState(() => {
    if (typeof data?.applyMarkup === "boolean") return data.applyMarkup;
    try {
      const cached = JSON.parse(
        sessionStorage.getItem(`refundQuoteAdmin:${data?._id}`) || "null",
      );
      if (typeof cached?.applyMarkup === "boolean") return cached.applyMarkup;
    } catch (_) {
      /* ignore */
    }
    return true;
  });

  async function handleSubmit() {
    const passengerPrice = (feeData?.passengerPrice || []).filter(
      (obj, i, arr) =>
        arr?.findIndex((ob) => obj?.travelerType === ob?.travelerType) === i,
    );

    const deadline =
      paymentDeadline && typeof paymentDeadline?.toISOString === "function"
        ? paymentDeadline.toISOString()
        : paymentDeadline;

    const overTicket = passengerPrice.some((p) => {
      const { unitPrice, unitBase } = getPaxTypeUnitPricing(
        data,
        p?.travelerType,
      );
      const adj = resolveOriginalAppliedAdjustment(data);
      // Cap = original unit after stripping base markup
      const { underlyingValue } = reverseOriginalAdjustment(
        unitPrice,
        adj,
        unitBase,
      );
      const cap = Number(underlyingValue || unitPrice || 0);
      const returns = (p?.breakDown || []).reduce((sum, row) => {
        if (
          !isRefundAmountLine(row?.name) &&
          !isRefundTaxLine(row?.name)
        ) {
          return sum;
        }
        const original = Number(
          row?.airlineAmount != null && row?.airlineAmount !== ""
            ? row.airlineAmount
            : row?.amount || 0,
        );
        return sum + Math.max(0, original);
      }, 0);
      return returns - cap > 0.0001;
    });
    if (overTicket) {
      enqueueSnackbar(
        "Base Return + Tax Return cannot exceed the original unit fare.",
        { variant: "error" },
      );
      setOpenConfirm(false);
      return;
    }

    // amount = agent (after quote Fixed/%); airlineAmount = airline original.
    // markupVariant/Type/Value/targets also sent for the backend.
    const reqBody = {
      refundRequestId: data?._id,
      requestExpirationTime: deadline,
      passengerPrice: toOriginalTicketPassengerPrice(passengerPrice),
      applyMarkup: Boolean(applyMarkup),
    };

    setLoadings({ ...loadings, submit: true });
    const res = await setRefundFee(reqBody);
    setLoadings({ ...loadings, submit: false });

    if (res.return) {
      try {
        sessionStorage.setItem(
          `refundQuoteAdmin:${data?._id}`,
          JSON.stringify({
            savedAt: Date.now(),
            passengerPrice,
            remark: details,
            applyMarkup: Boolean(applyMarkup),
          }),
        );
      } catch (_) {
        /* ignore quota / private mode */
      }
      enqueueSnackbar("Quote Complete.", { variant: "success" });
      reload && reload();
    } else if (
      (Number(res?.code) === 715 || isRefundNotApprovedError(res?.msg)) &&
      (hasRefundQuote(data) || isRefundQuotedStatus(data?.status))
    ) {
      setApiError({
        title: "Could not update quote",
        text: "Re-quote on RefundQuoted should be allowed and resets expiry. If this keeps failing, check with backend — you may still need Accept first on some statuses.",
      });
    } else if (Number(res?.code) === 715 || isRefundNotApprovedError(res?.msg)) {
      setApiError({
        title: "Accept this request first",
        text: "Approve / Accept the refund in Intraverse, then enter or edit the quote.",
      });
    } else setApiError(getStaffApiErrorCopy(res));
  }

  function handlePriceChange(next) {
    setFeeData(next);
  }

  const canShowBottomActions = useMemo(() => {
    const arr = feeData?.passengerPrice || [];
    if (!arr.length) return false;

    const unique = arr.filter(
      (obj, i, a) =>
        a.findIndex((ob) => ob?.travelerType === obj?.travelerType) === i,
    );

    if (!unique.length) return false;

    return unique.every((p) => {
      const bd = p?.breakDown || [];
      // Need at least Base Return or Tax Return to form a refund quote
      return bd.some(
        (x) => x?.isDeducted === false && Number(x?.amount || 0) > 0,
      );
    });
  }, [feeData?.passengerPrice]);

  // Preview uses FE markup estimate until backend returns Intraverse Markup.
  const quoteTotals = useMemo(() => {
    const local =
      feeData?.passengerPrice?.length > 0
        ? feeData.passengerPrice
        : data?.passengerPrice;
    const server = data?.passengerPrice || [];
    const merged = (local || []).map((p) => {
      const fromServer = server.find(
        (s) => s?.travelerType === p?.travelerType,
      );
      const backendMarkup =
        getIntraverseMarkupFromBreakdown(fromServer?.breakDown) ??
        getIntraverseMarkupFromBreakdown(p?.breakDown) ??
        p?.intraverseMarkup ??
        null;
      return {
        ...p,
        intraverseMarkup: backendMarkup,
      };
    });
    return getRefundQuoteSummary(data, merged);
  }, [data, feeData?.passengerPrice]);

  // Backend payable wins once the quote has been priced.
  const backendPayable = Number(
    data?.financials?.netRefundToAgent ??
      data?.payable ??
      data?.paymentAmount ??
      0,
  );
  const hasBackendPayable =
    data?.priceReceived ||
    data?.isQuoted ||
    backendPayable > 0.0001;
  const totalRefundAllPassengers = hasBackendPayable
    ? backendPayable
    : quoteTotals.finalRefundAmount;
  // Signed: >0 we keep (markup), <0 extra to agent (markdown).
  const markupEffect =
    Number(quoteTotals.airlineRefund || 0) -
    Number(quoteTotals.finalRefundAmount || 0);

  return (
    <>
      <div className="flex flex-col max-h-[80vh] overflow-hidden w-full max-w-full">
        <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-hide">
          <RelatedDocsPanel
            data={data}
            title="Supporting documents"
            empty
          />
          <div className="shrink-0">
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <h3 className="text-sm font-semibold text-[#0F172A]">Quote</h3>
              <p className="text-[11px] text-[#6D7C94] text-right">
                Strip base markup → returns − fees → add markup back.
              </p>
            </div>
            <PriceBreakdown data={data} callback={handlePriceChange} />
          </div>

          <div className="shrink-0 rounded-xl border border-[#E8EEF5] bg-white p-3 flex flex-col gap-3">
            <ExpiryInput callback={(d) => setPaymentDeadline(d)} />

            <div className="border-t border-[#E8EEF5] pt-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                    Apply configured markup
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-0.5 leading-snug">
                    When on, backend subtracts the platform&apos;s configured
                    refund markup from what the agent receives. Separate from
                    the Fixed/% markup you set on Base/Tax Return above.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={applyMarkup}
                  onClick={() => setApplyMarkup((v) => !v)}
                  className={`relative shrink-0 h-7 w-12 rounded-full transition ${
                    applyMarkup ? "bg-primary1" : "bg-[#CBD5E1]"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition ${
                      applyMarkup ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
              <div className="mt-1 text-[11px] font-medium text-[#0F172A]">
                {applyMarkup
                  ? "On — configured refund markup will be applied"
                  : "Off — agent gets quote amounts without config markup"}
              </div>
            </div>

            <div className="border-t border-[#E8EEF5] pt-2.5">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                  Remarks
                </label>
                <span className="text-[10px] text-[#94A3B8]">Optional</span>
              </div>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Notes for this quote…"
                rows={2}
                className="w-full min-h-[56px] max-h-28 px-2.5 py-1.5 text-sm border border-[#DBDBDB] rounded-lg text-[#0F172A] placeholder:text-[#A4B1C1] resize-y focus:outline-none focus:border-[#94A3B8]"
              />
            </div>
          </div>
        </div>

        <Modal3
          open={openConfirm}
          setOpen={() => setOpenConfirm(false)}
          title="Confirm Quote"
        >
          <div className="flex flex-col gap-3 p-4 max-w-md">
            <p className="text-sm text-[#64748B]">
              Submit this quote? Estimated payable is from what you entered.
              Current payable is what the backend has on the request after a
              prior quote.
              {applyMarkup
                ? " Configured platform markup applies on submit."
                : " Configured platform markup is off."}
            </p>
            <div className="rounded-lg bg-[#F8FAFC] border border-[#E8EEF5] px-3 py-2.5 space-y-2">
              {[
                {
                  label: "Selected sold value",
                  value: formatMoney(quoteTotals.refundRunSum),
                },
                {
                  label: "Deductible (original − Base − Tax)",
                  value: `−${formatMoney(quoteTotals.totalDeductibleField || 0)}`,
                  valueClass: "text-rose-700",
                },
                Number(quoteTotals.totalOpsFees || 0) > 0.0001
                  ? {
                      label: "Penalty + supplier fee",
                      value: `−${formatMoney(quoteTotals.totalOpsFees)}`,
                      valueClass: "text-rose-700",
                    }
                  : null,
                {
                  label: "Airline refund (Base + Tax)",
                  value: formatMoney(
                    quoteTotals.airlineSideTotal ?? quoteTotals.airlineRefund,
                  ),
                },
                Number(quoteTotals.totalHeldAdjustment || 0) > 0.0001
                  ? {
                      label: quoteTotals.rows?.some(
                        (r) => r.markupSource === "backend",
                      )
                        ? "Intraverse Markup (from quote)"
                        : "Booking markup (estimate)",
                      value: `+${formatMoney(quoteTotals.totalHeldAdjustment)}`,
                      valueClass: "text-emerald-700",
                    }
                  : null,
                markupEffect > 0.0001
                  ? {
                      label: "Quote markup kept",
                      value: `−${formatMoney(markupEffect)}`,
                      valueClass: "text-emerald-700",
                    }
                  : null,
                markupEffect < -0.0001
                  ? {
                      label: "Quote markdown",
                      value: `+${formatMoney(Math.abs(markupEffect))}`,
                      valueClass: "text-amber-700",
                    }
                  : null,
                {
                  label: "Ticketing fee retained",
                  value: formatMoney(quoteTotals.ticketingFeeRetained),
                },
                {
                  label: "Refund service fee",
                  value: formatMoney(quoteTotals.serviceFee || 0),
                },
                {
                  label: "Configured markup",
                  value: applyMarkup ? "Yes" : "No",
                  valueClass: applyMarkup
                    ? "text-emerald-700"
                    : "text-[#64748B]",
                },
              ]
                .filter(Boolean)
                .map((row) => (
                  <div
                    key={row.label}
                    className="flex items-baseline gap-3 text-xs"
                  >
                    <span className="text-[#6D7C94] shrink-0">{row.label}</span>
                    <span
                      className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[12px]"
                      aria-hidden
                    />
                    <span
                      className={`font-medium tabular-nums shrink-0 ${
                        row.valueClass || "text-[#0F172A]"
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              <div className="flex items-baseline gap-3 border-t border-[#E8EEF5] pt-2 mt-0.5">
                <span className="text-xs font-semibold text-[#0F172A] shrink-0">
                  Estimated payable
                </span>
                <span
                  className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[12px]"
                  aria-hidden
                />
                <span className="text-sm font-semibold tabular-nums text-[#0F172A] shrink-0">
                  {formatMoney(quoteTotals.finalRefundAmount)}
                </span>
              </div>
              {hasBackendPayable ? (
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-semibold text-[#0F172A] shrink-0">
                    Current payable
                  </span>
                  <span
                    className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[12px]"
                    aria-hidden
                  />
                  <span className="text-sm font-semibold tabular-nums text-[#0F172A] shrink-0">
                    {formatMoney(backendPayable)}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setOpenConfirm(false)}
                className="h-8 rounded-lg px-3 border border-[#E2E8F0] text-xs font-semibold text-[#334155] bg-white hover:bg-[#F8FAFC]"
              >
                Cancel
              </button>
              <Button1 loading={loadings?.submit} onClick={handleSubmit}>
                Confirm
              </Button1>
            </div>
          </div>
        </Modal3>

        {canShowBottomActions ? (
          <div className="shrink-0 border-t border-[#E8EEF5] bg-white pt-2.5 mt-2 w-full max-w-full overflow-hidden">
            <div className="flex items-center justify-between gap-3 w-full min-w-0">
              <div className="flex flex-col leading-tight min-w-0 flex-1 overflow-hidden">
                <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                  {hasBackendPayable
                    ? "Current payable"
                    : "Estimated payable"}
                </span>
                <span className="text-sm font-semibold text-[#0F172A]">
                  {formatMoney(totalRefundAllPassengers)}
                </span>
                <span className="text-[10px] text-[#6D7C94] truncate block max-w-full">
                  {hasBackendPayable
                    ? `Estimated payable ${formatMoney(
                        quoteTotals.finalRefundAmount,
                      )}`
                    : `Base+Tax − fees${
                        markupEffect > 0.0001
                          ? ` · Quote keep ${formatMoney(markupEffect)}`
                          : markupEffect < -0.0001
                            ? ` · Quote markdown ${formatMoney(
                                Math.abs(markupEffect),
                              )}`
                            : ""
                      }`}
                  {" · "}
                  Ticket fee kept {formatMoney(quoteTotals.ticketingFeeRetained)}
                  {applyMarkup ? " · Config markup on" : " · Config markup off"}
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setOpenConfirm(false)}
                  className="h-8 rounded-lg px-3 border border-[#E2E8F0] text-xs font-medium text-[#475569] bg-white hover:bg-[#F8FAFC]"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={loadings?.submit}
                  onClick={() => setOpenConfirm(true)}
                  className={`h-8 rounded-lg px-4 text-xs font-semibold text-white ${
                    loadings?.submit
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-primary1 hover:opacity-90"
                  }`}
                >
                  Confirm quote
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
    </>
  );
}
